import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import type { Department } from "@/types/domain";

// A fixed, reused palette (not random per render) so the same department
// always gets the same colour across a session. Spans the brand's purple/pink
// family (Royal/Bright/Dark Purple, Pink) plus teal/amber/indigo/neutral for
// real categorical variety - see tailwind.config.ts's header comment.
const PALETTE = ["#6c3fd4", "#ea4c89", "#8b5cf6", "#bb1656", "#0d9488", "#b45309", "#4f46e5", "#6e6d7a"];

export function DepartmentDonut({ departments }: { departments: Department[] }) {
  const rows = departments
    .filter((d) => (d.employee_count ?? 0) > 0)
    .sort((a, b) => (b.employee_count ?? 0) - (a.employee_count ?? 0));
  const total = rows.reduce((sum, d) => sum + (d.employee_count ?? 0), 0);

  if (rows.length === 0) {
    return <p className="flex h-56 items-center justify-center text-sm text-slate-400">No employees assigned to a department yet.</p>;
  }

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row">
      <div className="h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          {/* accessibilityLayer=false: drops the chart root <svg>'s own
              tabIndex/role="application", so clicking the chart itself never
              focuses it. That alone wasn't enough - Recharts' <Pie> ALSO
              gives its own wrapping <g> a separate, independent
              rootTabIndex=0 by default, and clicking a segment focuses THAT
              instead, which is what was actually drawing the browser's
              unstyled default focus rectangle around the donut (Recharts
              never sets outline:none on either). rootTabIndex={-1} below
              closes that second path. Both are safe to disable: the legend
              list right next to this chart already exposes the same
              department/count data as ordinary, keyboard- and
              screen-reader-accessible HTML. */}
          <PieChart accessibilityLayer={false}>
            <Pie
              data={rows}
              dataKey="employee_count"
              nameKey="name"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              // A real but small department (e.g. 1 person in a large
              // org) would otherwise render as a near-invisible sliver,
              // easy to mistake for a rendering glitch rather than
              // genuine data - a minimum angle keeps every department
              // visibly a wedge.
              minAngle={3}
              strokeWidth={0}
              rootTabIndex={-1}
            >
              {rows.map((d, i) => (
                <Cell key={d.id} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="w-full min-w-0 flex-1 space-y-2">
        {rows.map((d, i) => (
          <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-slate-600">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
              <span className="truncate">{d.name}</span>
            </span>
            <span className="shrink-0 font-medium text-slate-800">
              {d.employee_count} <span className="text-slate-400">({(((d.employee_count ?? 0) / total) * 100).toFixed(1)}%)</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
